import { useState } from "react";
import { FormRow, FormSection, HelpList, Pill, Switch } from "@omnabase/ui";
import type { AiEngine as Engine, AiKeyRead, TeamConnection } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useCan } from "../../lib/access";
import { aiPlanLine, engineNow, radioOf } from "../../lib/aiKey";
import { connectedMembersLine, START_LINE } from "../../lib/connections";
import { useFetch } from "../../lib/useFetch";
import { useNav, useWs } from "../../lib/nav";
import { useFormState } from "../../lib/save";
import { useSession } from "../../shell/store";
import { PlansAction } from "./PlanGate";
import { AiKeyCard, UnusedKeyFrame } from "./AiKeyCard";

// Settings › AI › Engine (AI settings round one, 2026-10-18; round two, your own key; round three, No AI and the key inside its card,
// 2026-10-19): who does the AI work, as a radio of the kit's choice cards: "No AI" (only the inbox's own sorting runs), "OmnaBase AI" (our
// models, paid in credits) and "Your own key" (the workspace's key, billed by its provider). The radio shows the engine that runs, as the API
// answers it (`effective`): on a plan without AI that is No AI, and the two paid cards are locked with the plan and "See plans". Every card
// saves at the click (2026-10-21, brief `key-choice-and-mail-order.md`): "Your own key" without a working key is chosen too, the AI then paused,
// and its card opens below it with the warning, the provider, the key and "Check and save", which stores the key and lifts the pause; with a
// working key the card holds the key's controls; a key stored while another engine runs (or on a plan without AI) shows
// its row in a frame joined below the card, with its own "Remove key" (round four, 2026-10-20). Then the connector line: members' own AI beside the engine, with the administrator's switch and the MCP tab.

/** what a member's connected AI may do, one rule per line */
const MEMBER_AI_RULES = ["It sees what that member sees and acts with their rights", "It writes notes and drafts marked as their AI's", "It can send replies when its member confirms them", START_LINE];
const INTRO = "Who does the AI work: drafts, investigations and suggestions, or nothing. One choice at a time.";
const KEY_SUB = "Your key does the work; your provider bills you directly, no credits are used.";

/** the engine for holders of `aiSettings`; the connector line also for holders of `team` (the switch is theirs too, M9 round three) */
export function AiEngine() {
  const can = useCan();
  return <>{can("aiSettings") && <EngineAndKey />}{(can("aiSettings") || can("team")) && <Connector />}</>;
}

/** the radio and the key's card share one read of the key (GET /ai/key): which key, whether it works, its models; the radio itself shows the
 *  workspace read's engine, which every change of the engine or the key reloads */
function EngineAndKey() {
  const { workspace } = useSession();
  const url = `/api/v1/w/${workspace.slug}/ai/key`;
  const key = useFetch<AiKeyRead>(url);
  const [read, setRead] = useState<AiKeyRead | null>(null);
  const refresh = async () => { try { setRead(await api.get<AiKeyRead>(url)); } catch { /* the card keeps the last read */ } };
  return <EngineChoice read={read ?? key.data} onRead={setRead} onSaved={refresh} />;
}

/** the radio: every card saves at the click and the radio moves at once, without waiting for the key's read; after the save the workspace read
 *  and then the key's read are asked again, so the card shows what the server says (a paused key's warning at once, no reload) */
function EngineChoice({ read, onRead, onSaved }: { read: AiKeyRead | null; onRead: (r: AiKeyRead) => void; onSaved: () => Promise<void> }) {
  const { workspace, reload } = useSession();
  const [picked, setPicked] = useState<Engine | null>(null); // the radio moves at the click, until the workspace read answers again
  const [hint, setHint] = useState<string | null>(null);
  const engine = picked ?? radioOf(engineNow(workspace)); // what runs, as the API answers it (the workspace read, reloaded after every change)
  const lock = workspace.plan.features.ai ? null : workspace.plan.wayUp.ai?.name ?? "Locked";
  const choose = async (next: Engine) => {
    if (next === engine) return;
    setPicked(next); setHint(null);
    try { await api.patch(`/api/v1/w/${workspace.slug}/settings`, { aiEngine: next }); await reload(); void onSaved(); }
    catch (e) { setHint((e as Error).message); }
    finally { setPicked(null); }
  };
  const panel = read && engine === "key" && !lock; // the key's card opened below "Your own key"
  const unused = !!read?.key && !panel; // a stored key that runs for nobody: its row joined below the card (also locked, decision 4)
  return (
    <FormSection title="Engine" audience="admins" intro={INTRO}>
      <div role="radiogroup" aria-label="Engine" className="ob-mail-step__choices">
        <EngineCard title="No AI" sub="No drafts, investigations or suggestions." on={engine === "none"} onClick={() => void choose("none")} />
        <EngineCard title="OmnaBase AI" sub={`Our models, paid in credits: ${workspace.credits.balance} left · 1 credit a sent reply, investigations from 1 credit.`} on={engine === "omnabase"} lock={lock} onClick={() => void choose("omnabase")} />
        <EngineCard title="Your own key" sub={KEY_SUB} on={engine === "key"} open={!!panel || unused} lock={lock} onClick={() => void choose("key")} />
      </div>
      {panel && <AiKeyCard read={read} chosen={engine === "key"} onRead={onRead} />}
      {unused && read && <UnusedKeyFrame read={read} onRead={onRead} />}
      {lock && <p className="ob-form-row__help">{aiPlanLine(workspace.plan.wayUp.ai, workspace.plan.name)} <PlansAction /></p>}
      {hint && <p className="ob-form-row__warning" role="alert">{hint}</p>}
    </FormSection>
  );
}

/** one engine as a choice card; a locked one names the plan that unlocks it inside the card */
function EngineCard({ title, sub, on, open, lock, onClick }: { title: string; sub: string; on: boolean; open?: boolean; lock?: string | null; onClick: () => void }) {
  return (
    <button type="button" role="radio" aria-checked={on} aria-label={title} disabled={!!lock} className={`ob-choice-opt${on ? " ob-choice-opt--on" : ""}${open ? " ob-choice-opt--open" : ""}`} onClick={onClick}>
      <span className="ob-choice-opt__radio" /><span className="ob-choice-opt__text"><span className="ob-choice-opt__title">{title}{lock && <Pill>{lock}</Pill>}</span><span className="ob-choice-opt__sub">{sub}</span></span>
    </button>
  );
}

/** the administrator's switch "Members may connect their own AI" (off until decided; on a plan without the feature it waits), how many
 *  members connected, and the way to Your own AI's page (its own section under You since the polish round, 2026-10-31) */
function Connector() {
  const nav = useNav(); const { ws } = useWs();
  const { workspace, reload, toast } = useSession();
  const list = useFetch<{ rows: TeamConnection[] }>(`/api/v1/w/${workspace.slug}/connections`);
  const mcp = workspace.plan.features.mcp;
  const [on, setOn] = useFormState(workspace.settings.aiConnections, workspace.id); // the page is the source of truth while open (lib/save.ts): the switch moves at the click
  const tab = ws("/settings/your-ai");
  const toggle = async (v: boolean) => {
    setOn(v);
    try {
      await api.patch(`/api/v1/w/${workspace.slug}/settings`, { aiConnections: v }); await reload();
      toast(v ? "Members may connect their own AI · each sets it up under Settings › Your own AI" : "Connected AIs are off · every connection stops answering until this is on again");
    } catch (e) { setOn(!v); toast((e as Error).message, { kind: "error" }); }
  };
  return (
    <FormSection title="Your own AI (MCP) beside the engine" audience="admins" intro="Members may connect the AI app they already use. It works with their rights and uses no credits.">
      <FormRow label="Members may connect their own AI" help={<>Whether members may make a key and connect their own AI to this workspace.<HelpList items={MEMBER_AI_RULES} /></>}>
        <Switch checked={on && mcp} disabled={!mcp} onChange={(v) => void toggle(v)} label="Members may connect their own AI" />
      </FormRow>
      <a className="ob-form__link-row" href={tab} onClick={(e) => nav(tab, e)}><span>{list.data ? connectedMembersLine(list.data.rows) : "Members' connections"}</span><span className="ob-form__link-row-meta">Open Your own AI</span></a>
    </FormSection>
  );
}
